import React, { useState } from 'react';
import { theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconCompass, IconCalendarEvent, IconMessageCircle, IconUser } from '@tabler/icons-react';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { useScrollChrome } from '../../hooks/useScrollChrome';
import { useIncomingBookingsBadge } from '../../contextProviders/IncomingBookingsProvider';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { useServiceProviderProfile } from '../../contextProviders/ServiceProviderProfileProvider';
import { CORAL } from '../../theme/creamyTokens';
import { visibleHostTools, hostToolPath } from '../../constants/hostToolsNav';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import NavBadge from '../NavBadge';
import ActionSheet, { ActionSheetItem } from '../ActionSheet';

// This bar's own real rendered height (57px: TabButton's 56px minHeight + the
// 1px top border) plus its safe-area padding -- exported so other fixed
// mobile chrome that needs to sit directly above this bar (e.g.
// HeavyDutyMachineryDetail's floating "Reservar ahora" button) can position
// itself against the same number instead of guessing/duplicating it.
export const MOBILE_BOTTOM_NAV_HEIGHT = 'calc(57px + env(safe-area-inset-bottom, 0px))';

type TabKey = 'explore' | 'bookings' | 'messages' | 'profile' | '';

const getActiveTab = (pathname: string): TabKey => {
  if (pathname.startsWith('/heavy-duty-machinery/bookings')) return 'bookings';
  if (pathname.startsWith('/heavy-duty-machinery/messages')) return 'messages';
  if (pathname.startsWith('/settings') || pathname.startsWith('/account')) return 'profile';
  if (pathname === '/heavy-duty-machinery' || /^\/heavy-duty-machinery\/[^/]+$/.test(pathname)) return 'explore';
  return '';
};

interface TabButtonProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  activeColor: string;
  inactiveColor: string;
}

const TabButton: React.FC<TabButtonProps> = ({ icon, label, active, onClick, activeColor, inactiveColor }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      minHeight: 56,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: active ? activeColor : inactiveColor,
    }}
  >
    {icon}
    <span style={{ fontSize: 11, fontWeight: active ? 600 : 400 }}>{label}</span>
  </button>
);

interface MobileBottomNavProps {
  // Set by AppLayout (see its own hideBottomNav prop) for screens that need
  // the bar gone entirely rather than just auto-hidden on scroll -- e.g. an
  // open Messages thread, where the bar would otherwise sit on top of that
  // screen's own keyboard-pinned input (see Messages.tsx). Combined with
  // scrollingDown below rather than replacing it, so scroll-based hiding
  // still works normally everywhere this isn't set.
  hidden?: boolean;
}

/**
 * Mobile-only bottom tab bar, replacing AppHeader's hamburger + side Drawer
 * on phones (see useAppBreakpoint) -- desktop/tablet keep that nav exactly
 * as it was (see AppHeader's own doc comment). Mounted unconditionally by
 * AppLayout; renders nothing outside isMobile so it's a no-op there instead
 * of needing every caller to guard it.
 *
 * The center slot is the "+" add-menu button (see addMenuItems below) --
 * this used to be a plain "Máquinas" tab straight to /heavy-duty-machinery/mine,
 * and separately there used to be an elevated "+" here that got moved into
 * AppHeader's mobile header instead, because it floated independently of
 * this bar and ended up covering page content on some screens (e.g.
 * Messages' own input). This round folds both of those back into one:
 * the "+" lives here now, but as part of this <nav> itself (a slightly
 * larger circle poking a few px above the bar's own top edge, not a
 * separately-positioned floating element) -- it slides away together with
 * the rest of the bar (see the transform below), so it never persists over
 * content the way the old floating one did. AppHeader's own "+" is gone;
 * this is the only entry point into the add-menu now, reachable from every
 * screen since the bar itself always is. "Mis máquinas" (added here, unlike
 * before) plugs the gap left by removing the direct tab -- one entry, same
 * "Mis X" pattern as every other catalog in this menu (list-first, with its
 * own "add" button/empty-state CTA inside), not a separate "Publicar
 * maquinaria" shortcut alongside it -- see addMenuItems below for why that
 * used to be two entries and no longer is.
 *
 * Slides down and fades out while the page is scrolling down, and back into
 * view on scroll-up (see useScrollChrome) -- unlike AppHeader, which stays
 * fixed and only ever gains a bottom-edge shadow.
 */
const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ hidden }) => {
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollingDown } = useScrollChrome();
  const { unseenCount } = useIncomingBookingsBadge();
  const { hasHostProfile, loading: hostProfileLoading } = useHostProfile();
  const { hasServiceProviderProfile } = useServiceProviderProfile();
  const [addSheetOpen, setAddSheetOpen] = useState(false);

  if (!isMobile) return null;

  const shouldHide = hidden || scrollingDown;

  const active = getActiveTab(location.pathname);

  // The host's own screens. Same list the desktop header's grid icon reads
  // (HOST_TOOLS_NAV), which is the point: this sheet and that dropdown were
  // written separately and had already drifted -- "Flota en vivo" and "Mi
  // depósito" existed on desktop and were unreachable from a phone at all,
  // because they were added to one list and not the other.
  const hostTools = visibleHostTools({ hasHostProfile, hasServiceProviderProfile }, 'mobileSheet');
  const addMenuItems: ActionSheetItem[] = hostTools.map(({ key, Icon, labelKey }) => ({
    key,
    icon: <Icon size={20} />,
    label: translate(t, labelKey),
  }));

  const handleAddMenuSelect = (key: string) => {
    setAddSheetOpen(false);
    const tool = hostTools.find((item) => item.key === key);
    if (tool) navigate(hostToolPath(tool, hasHostProfile));
  };

  const leftTabs: { key: TabKey; icon: React.ReactNode; labelKey: string; path: string }[] = [
    { key: 'explore', icon: <IconCompass size={22} />, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_EXPLORE, path: '/heavy-duty-machinery' },
    { key: 'bookings', icon: <IconCalendarEvent size={22} />, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_BOOKINGS_SHORT, path: '/heavy-duty-machinery/bookings/mine' },
  ];
  const rightTabs: { key: TabKey; icon: React.ReactNode; labelKey: string; path: string }[] = [
    { key: 'messages', icon: <IconMessageCircle size={22} />, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_NAV_BUTTON, path: '/heavy-duty-machinery/messages' },
    { key: 'profile', icon: <IconUser size={22} />, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_PROFILE, path: '/account' },
  ];

  const renderTab = (tab: { key: TabKey; icon: React.ReactNode; labelKey: string; path: string }) => (
    <TabButton
      key={tab.key}
      icon={
        tab.key === 'bookings' ? (
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            {tab.icon}
            <span style={{ position: 'absolute', top: -4, right: -8 }}>
              <NavBadge count={unseenCount} />
            </span>
          </span>
        ) : (
          tab.icon
        )
      }
      label={translate(t, tab.labelKey)}
      active={active === tab.key}
      onClick={() =>
        // The badge only ever lights up for an unseen incoming (host)
        // booking (see IncomingBookingsProvider) -- route straight to
        // MyBookings' host view instead of its 'guest' default, so the
        // tap actually lands on what the badge was telling the host
        // about instead of showing their own guest bookings first.
        navigate(tab.key === 'bookings' && unseenCount > 0 ? '/heavy-duty-machinery/bookings/mine?view=host' : tab.path)
      }
      activeColor={CORAL}
      inactiveColor={token.colorTextSecondary}
    />
  );

  return (
    <nav
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: 'flex',
        background: token.colorBgContainer,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        // Real safe-area support today (WebView), plain 0 fallback elsewhere.
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // Positive translateY (down and out), opposite sign from AppHeader's
        // old hide behavior, since this bar sits at the bottom of the screen.
        transform: shouldHide ? 'translateY(100%)' : 'translateY(0)',
        opacity: shouldHide ? 0 : 1,
        transition: 'transform 220ms ease, opacity 220ms ease',
        pointerEvents: shouldHide ? 'none' : undefined,
      }}
    >
      {leftTabs.map(renderTab)}
      <button
        type="button"
        aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ADD_MENU_TITLE)}
        onClick={() => setAddSheetOpen(true)}
        disabled={hostProfileLoading}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 56,
          border: 'none',
          background: 'none',
          cursor: hostProfileLoading ? 'default' : 'pointer',
        }}
      >
        <span
          style={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            background: CORAL,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Pokes a few px above the bar's own top edge -- still part of
            // this <nav>, not a separately-positioned floating element (see
            // this component's own doc comment for why that distinction
            // matters), so it hides/shows together with the rest of the bar.
            marginTop: -18,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          <PlusOutlined style={{ fontSize: 22 }} />
        </span>
      </button>
      {rightTabs.map(renderTab)}
      <ActionSheet
        open={addSheetOpen}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ADD_MENU_TITLE)}
        items={addMenuItems}
        onSelect={handleAddMenuSelect}
        onClose={() => setAddSheetOpen(false)}
      />
    </nav>
  );
};

export default MobileBottomNav;
